import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkles, Users, MousePointerClick, MessageCircle, GraduationCap, RefreshCw, Star } from "lucide-react";
import { Button } from "../components/ui/button";

function Stat({ label, value, sub, testId, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-foreground/10 p-5 bg-background" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-foreground/50">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" />}
      </div>
      <div className="mt-1 font-serif text-3xl">{value ?? 0}</div>
      {sub && <div className="text-xs text-foreground/60 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminAstrologyTools() {
  const [stats, setStats] = useState({});
  const [leads, setLeads] = useState([]);
  const load = () => {
    api.get("/admin/astrology/stats").then((r) => setStats(r.data));
    api.get("/admin/astrology/leads?limit=100").then((r) => setLeads(r.data.items || []));
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-astro-tools" className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-4xl">Astrology Tools</h1>
          <p className="text-foreground/60 mt-1">Usage of the free Kundali &amp; Nakshatra tools and the leads they generate.</p>
        </div>
        <Button variant="outline" onClick={load} className="rounded-full"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Total generations" value={stats.total} icon={Sparkles} testId="astro-stat-total" />
        <Stat label="Kundalis" value={stats.by_tool?.kundali} sub="full charts" icon={Star} testId="astro-stat-kundali" />
        <Stat label="Nakshatra searches" value={stats.by_tool?.nakshatra} icon={Sparkles} testId="astro-stat-nakshatra" />
        <Stat label="Leads captured" value={stats.total} sub="today: " icon={Users} testId="astro-stat-leads" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Today" value={stats.day} icon={Sparkles} testId="astro-stat-day" />
        <Stat label="Last 7 days" value={stats.week} testId="astro-stat-week" />
        <Stat label="Last 30 days" value={stats.month} testId="astro-stat-month" />
        <Stat label="Consultation clicks" value={stats.consultation_clicks} sub={`WA ${stats.whatsapp_clicks || 0} · Courses ${stats.course_clicks || 0}`} icon={MousePointerClick} testId="astro-stat-cta" />
      </div>

      {/* Leads table */}
      <div className="rounded-3xl border border-foreground/10 overflow-hidden bg-background">
        <div className="p-5 border-b border-foreground/10 flex items-center justify-between">
          <div>
            <div className="eyebrow">Recent Leads</div>
            <div className="text-xs text-foreground/50 mt-1">Sensitive birth details (DOB, exact time, coords) are stored server-side and never surfaced here.</div>
          </div>
          <div className="text-xs text-foreground/60">{leads.length} rows</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-[#F4EFE6]/60 dark:bg-[#0F284B]/60 text-left">
              <tr>
                {["Name", "Tool", "Place", "Timezone", "Interest", "Actions", "Created"].map((h) => (
                  <th key={h} className="p-3 text-[10px] uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-foreground/50">No leads yet — share the tools to start collecting.</td></tr>
              ) : leads.map((l) => (
                <tr key={l.id} data-testid={`astro-lead-${l.id}`} className="border-t border-foreground/10">
                  <td className="p-3 font-medium">{l.name || <span className="text-foreground/40 italic">anonymous</span>}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold ${l.tool_used === "kundali" ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-emerald-500/15 text-emerald-600"}`}>
                      {l.tool_used}
                    </span>
                  </td>
                  <td className="p-3 text-xs">{l.birth_place || "—"}</td>
                  <td className="p-3 text-xs font-mono">{l.timezone || "—"}</td>
                  <td className="p-3">
                    {l.consultation_interest
                      ? <span className="text-emerald-600 text-xs font-semibold">● Interested</span>
                      : <span className="text-foreground/40 text-xs">—</span>}
                  </td>
                  <td className="p-3 text-xs">
                    <span className="inline-flex items-center gap-1">
                      <MousePointerClick className="w-3 h-3" />{l.clicks?.consultation || 0}
                      <MessageCircle className="w-3 h-3 ml-2 text-[#25D366]" />{l.clicks?.whatsapp || 0}
                      <GraduationCap className="w-3 h-3 ml-2" />{l.clicks?.course || 0}
                    </span>
                  </td>
                  <td className="p-3 text-[11px] text-foreground/60">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
